import {
  addEarnings,
  buy,
  emptyEconomy,
  itemUnlocked,
  modsFromUpgrades,
  SHOP,
  type EconomyState,
} from './core/economy';
import { getResult, isUnlocked, mergeProgress, recordCompletion } from './core/progress';
import { World } from './core/world';
import { buildLevel, ZONES, zoneLevels } from './game/registry';
import { getIdToken } from './platform/account';
import { createKeyboard } from './platform/keyboard';
import { startLoop } from './platform/loop';
import { mountLoginGate } from './platform/loginGate';
import { loadRemote, saveRemote } from './platform/progressApi';
import { GUEST_SCOPE, loadProgress, saveProgress } from './platform/storage';
import { createTelemetry } from './platform/telemetry';
import { followCamera, type Camera } from './render/camera';
import { renderOverworld } from './render/overworld';
import { render } from './render/renderer';
import { renderShop } from './render/shop';
import { loadSprites } from './render/sprites';
import { renderZoneSelect } from './render/zoneselect';

const canvas = document.getElementById('game') as HTMLCanvasElement | null;
if (!canvas) throw new Error('canvas#game not found');
const ctx = canvas.getContext('2d');
if (!ctx) throw new Error('2d context unavailable');

// World-unit viewport (unchanged gameplay view) rendered at SCALE× for a larger,
// higher-resolution, smoothly-scaled picture that fills the browser.
const SCALE = 2;
const VIEW_W = 640;
const VIEW_H = 224;
canvas.width = VIEW_W * SCALE;
canvas.height = VIEW_H * SCALE;
ctx.imageSmoothingEnabled = true; // smooth (non-pixelated) scaling

// New Relic Browser when configured (see index.html / docs/TELEMETRY.md), else console.
const telemetry = createTelemetry();
telemetry.gameEvent('game_start', {});

const keyboard = createKeyboard();
const cam: Camera = { x: 0, y: 0 };
// Progress is scoped per identity: 'guest' until signed in, then the username.
let scope = GUEST_SCOPE;
let progress = loadProgress(scope);

type Mode = 'zones' | 'overworld' | 'playing' | 'shop';
let mode: Mode = 'zones';
let world: World | null = null;
let currentTheme = 'forest';
let zoneIndex = 0;
let currentZoneId = ZONES[0].id;
let levelSel = 0;
let shopSelected = 0;

// Edge detection for menu navigation.
let prev = { left: false, right: false, jump: false, dash: false, up: false, down: false, back: false };

function economy(): EconomyState {
  return progress.economy ?? emptyEconomy();
}

/** Persist a new economy state into progress (local, scoped + remote). */
function setEconomy(next: EconomyState): void {
  progress = { ...progress, economy: next };
  saveProgress(progress, scope);
  pushRemote();
}

function zoneIsComplete(zoneId: string): boolean {
  return zoneLevels(zoneId).every((l) => getResult(progress, l.id).completed);
}
function completedZones(): Set<string> {
  return new Set(ZONES.filter((z) => zoneIsComplete(z.id)).map((z) => z.id));
}
function zoneUnlocked(i: number): boolean {
  return i === 0 || zoneIsComplete(ZONES[i - 1].id);
}

/** Reset menu cursors to the player's current progress (first incomplete zone). */
function recompute(): void {
  const z = ZONES.findIndex((zone) => !zoneIsComplete(zone.id));
  zoneIndex = z < 0 ? 0 : z;
  mode = 'zones';
}

/** Push current progress to the backend if signed in (fire-and-forget). */
function pushRemote(): void {
  void getIdToken().then((token) => {
    if (token) void saveRemote(token, progress).catch(() => undefined);
  });
}

// Login gate: hides the game until the user signs in or continues as guest. Progress
// is per-account — sign-in switches to the user's scope and takes their remote data as
// authoritative (merged only with that same user's cache); guest progress never bleeds in.
function useGuest(): void {
  scope = GUEST_SCOPE;
  progress = loadProgress(scope);
  recompute();
}

mountLoginGate({
  onSignedIn: async (token, username) => {
    scope = username;
    let next = loadProgress(scope); // this user's local cache (not guest)
    try {
      const remote = await loadRemote(token);
      next = mergeProgress(next, remote);
      await saveRemote(token, next);
    } catch {
      // backend unreachable — fall back to the user's local cache
    }
    progress = next;
    saveProgress(progress, scope);
    recompute();
  },
  onSignedOut: useGuest,
  onGuest: useGuest,
});

function enterZone(i: number): void {
  if (!zoneUnlocked(i)) return;
  zoneIndex = i;
  currentZoneId = ZONES[i].id;
  const lv = zoneLevels(currentZoneId);
  const f = lv.findIndex((l) => !getResult(progress, l.id).completed);
  levelSel = f < 0 ? 0 : f;
  mode = 'overworld';
  telemetry.gameEvent('overworld_open', {});
}

function startLevel(): void {
  const lv = zoneLevels(currentZoneId);
  const entry = lv[levelSel];
  if (!isUnlocked(progress, lv.map((l) => l.id), entry.id)) return;
  currentTheme = entry.theme;
  world = new World(buildLevel(entry), {
    telemetry,
    mods: modsFromUpgrades(economy()),
    collected: economy().collected,
  });
  mode = 'playing';
  telemetry.gameEvent('level_select', { level: entry.id });
}

recompute();

function tick(): void {
  const s = keyboard.state;
  const edge = (k: keyof typeof prev): boolean => Boolean(s[k]) && !prev[k];

  if (mode === 'zones') {
    if (edge('left')) zoneIndex = (zoneIndex - 1 + ZONES.length) % ZONES.length;
    if (edge('right')) zoneIndex = (zoneIndex + 1) % ZONES.length;
    if (edge('jump')) enterZone(zoneIndex);
  } else if (mode === 'overworld') {
    const lv = zoneLevels(currentZoneId);
    if (edge('left')) levelSel = (levelSel - 1 + lv.length) % lv.length;
    if (edge('right')) levelSel = (levelSel + 1) % lv.length;
    if (edge('jump')) startLevel();
    if (edge('dash')) {
      shopSelected = 0;
      mode = 'shop';
    }
    if (edge('back')) mode = 'zones';
  } else if (mode === 'shop') {
    if (edge('up')) shopSelected = (shopSelected - 1 + SHOP.length) % SHOP.length;
    if (edge('down')) shopSelected = (shopSelected + 1) % SHOP.length;
    if (edge('jump')) {
      const item = SHOP[shopSelected];
      const cz = completedZones();
      if (itemUnlocked(item, cz)) setEconomy(buy(economy(), item, cz.size));
    }
    if (edge('back')) mode = 'overworld';
  } else if (world) {
    if (edge('back')) {
      world = null;
      mode = 'overworld';
    } else {
      world.step(s);
      if (world.status === 'won') {
        progress = recordCompletion(progress, world.level.id, world.timeMs, world.deaths);
        // Bank newly-collected coins/diamonds and remember their keys so a replay
        // can't re-farm them (also persists + syncs).
        let eco = addEarnings(economy(), world.coinsCollected, world.diamondsCollected);
        eco = { ...eco, collected: [...new Set([...eco.collected, ...world.collectedKeys])] };
        setEconomy(eco);
        const lv = zoneLevels(currentZoneId);
        if (levelSel + 1 < lv.length) levelSel += 1;
        world = null;
        mode = 'overworld';
      }
    }
  }
  prev = {
    left: !!s.left,
    right: !!s.right,
    jump: !!s.jump,
    dash: !!s.dash,
    up: !!s.up,
    down: !!s.down,
    back: !!s.back,
  };
}

function frame(): void {
  // Render the world-unit viewport scaled up to the canvas backing store.
  ctx!.setTransform(SCALE, 0, 0, SCALE, 0, 0);
  if (mode === 'zones') {
    renderZoneSelect(ctx!, VIEW_W, VIEW_H, ZONES, zoneLevels, progress, zoneIndex, zoneUnlocked);
  } else if (mode === 'overworld') {
    const zone = ZONES[zoneIndex];
    renderOverworld(
      ctx!,
      VIEW_W,
      VIEW_H,
      zoneLevels(currentZoneId),
      progress,
      levelSel,
      economy(),
      zone.name,
      zone.theme,
    );
  } else if (mode === 'shop') {
    renderShop(ctx!, VIEW_W, VIEW_H, economy(), shopSelected, completedZones());
  } else if (world) {
    followCamera(cam, world.playerBox(), VIEW_W, VIEW_H, world.level);
    render(ctx!, world, cam, VIEW_W, VIEW_H, world.level.name, currentTheme);
  }
}

// Load sprites (graceful fallback to shapes if missing), then start the loop.
void loadSprites().finally(() => startLoop(tick, frame));
